import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useAdmin } from '@/context/AdminContext'
import { LayoutDashboard, FileText, Upload, User, LogOut, ArrowLeft, Shield } from 'lucide-react'
import { clsx } from 'clsx'
import { COMPANY } from '@/lib/constants'

const navItems = [
  { label: 'Dashboard', href: '/portal', icon: LayoutDashboard },
  { label: 'Documents', href: '/portal/documents', icon: FileText },
  { label: 'Upload', href: '/portal/upload', icon: Upload },
  { label: 'Profile', href: '/portal/profile', icon: User },
]

export default function PortalLayout() {
  const { user, logout } = useAuth()
  const { isAdmin } = useAdmin()
  const location = useLocation()
  const navigate = useNavigate()

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/')
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-primary-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="font-bold text-xl">
                {COMPANY.name}
              </Link>
              <span className="text-primary-300 text-sm hidden sm:inline">
                Client Portal
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-primary-200 text-sm hidden sm:inline">
                {user?.email}
              </span>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-primary-800 transition-colors text-sm"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar Navigation */}
          <aside className="md:w-64 flex-shrink-0">
            <nav className="bg-white rounded-xl shadow-md p-4">
              <ul className="space-y-1">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.href
                  return (
                    <li key={item.href}>
                      <Link
                        to={item.href}
                        className={clsx(
                          'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
                          isActive
                            ? 'bg-primary-50 text-primary-900 font-medium'
                            : 'text-gray-600 hover:bg-gray-50'
                        )}
                      >
                        <item.icon className="h-5 w-5" />
                        {item.label}
                      </Link>
                    </li>
                  )
                })}
              </ul>
              <div className="mt-4 pt-4 border-t space-y-1">
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="flex items-center gap-3 px-4 py-3 text-accent-600 hover:bg-accent-50 rounded-lg transition-colors text-sm font-medium"
                  >
                    <Shield className="h-4 w-4" />
                    Admin Portal
                  </Link>
                )}
                <Link
                  to="/"
                  className="flex items-center gap-3 px-4 py-3 text-gray-500 hover:text-gray-700 transition-colors text-sm"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Website
                </Link>
              </div>
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  )
}
